import get from "lodash.get";
import { Control, FieldPath, FieldValues, RegisterOptions, useFormState } from "react-hook-form";

import SinglePickerFormField from "src/components/Pickers/SinglePickerFormField.tsx";
import { useGetListFraudTypeQuery } from "src/graphql/anti-fraud/queries/alerts/ListFraudTypeQuery.ts";
import { useT } from "src/internationalization";
import { ConfListElement } from "src/types/confList";

export type FraudTypeSinglePickerFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  label: string;
  type?: string;
  helperText?: string;
  placeholder?: string;
  control: Control<TFieldValues>;
  ["aria-label"]?: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  name: TName;
  onLoad?: () => void;
  onError?: () => void;
};

export function FraudTypeSinglePickerFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  rules,
  helperText,
  control,
  label,
  onLoad,
  onError,
}: FraudTypeSinglePickerFormFieldProps<TFieldValues, TName>) {
  const { errors } = useFormState({ control });
  const error = get(errors, name);

  const {
    data: fraudTypesList,
    error: fetchError,
    loading: fetchLoading,
  } = useGetListFraudTypeQuery({ onCompleted: onLoad, onError });

  const t = useT();

  return (
    <SinglePickerFormField<ConfListElement, TFieldValues, TName>
      name={name}
      rules={rules}
      itemToString={(item) => {
        return item?.name ?? "";
      }}
      items={fraudTypesList?.elements ?? []}
      loading={fetchLoading}
      error={fetchError}
      invalidText={(error?.message as string) ?? t("component.picker.fraud.type.invalid")}
      titleText={label}
      helperText={helperText}
    />
  );
}
